import React, { memo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { Badge } from '@/components/ui/badge';
import { Bell, Edit2, Trash2, Link as LinkIcon } from 'lucide-react';
import ExecutionStatusOverlay from './ExecutionStatusOverlay';
import { getExecutionBorderClass, type ExecutionStatus } from './executionStatus';

interface NotifyStepNodeData {
  stepName: string;
  channel?: string;
  url?: string;
  payload?: any;
  executionStatus?: ExecutionStatus;
  onEdit?: () => void;
  onDelete?: () => void;
}

function NotifyStepNode({ data, selected }: NodeProps<NotifyStepNodeData>) {
  const channel = data.channel || 'webhook';

  return (
    <div
      className={`bg-white rounded-lg border-2 shadow-lg min-w-[280px] max-w-[320px] relative ${
        selected ? 'border-orange-500 shadow-orange-200' : 'border-orange-300'
      } ${getExecutionBorderClass(data.executionStatus)}`}
    >
      <ExecutionStatusOverlay status={data.executionStatus} />
      <Handle type="target" position={Position.Left} className="w-3 h-3 !bg-orange-500" />

      <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-3 py-2 rounded-t-md flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bell className="w-4 h-4" />
          <span className="font-semibold text-sm">Notify</span>
        </div>
        <div className="flex gap-1 nodrag nopan" style={{ pointerEvents: 'auto' }}>
          {data.onEdit && (
            <button
              type="button"
              className="h-6 w-6 p-1 flex items-center justify-center hover:bg-orange-600/20 rounded transition-colors cursor-pointer nodrag"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                data.onEdit?.();
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              title="Edit node"
            >
              <Edit2 className="w-3 h-3 text-white" style={{ pointerEvents: 'none' }} />
            </button>
          )}
          {data.onDelete && (
            <button
              type="button"
              className="h-6 w-6 p-1 flex items-center justify-center hover:bg-red-600/20 rounded transition-colors cursor-pointer nodrag"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                data.onDelete?.();
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              title="Delete node"
            >
              <Trash2 className="w-3 h-3 text-white" style={{ pointerEvents: 'none' }} />
            </button>
          )}
        </div>
      </div>

      <div className="p-3 space-y-2">
        <div className="font-semibold text-sm text-gray-900">{data.stepName}</div>

        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-xs">
            {channel}
          </Badge>
          {data.url && (
            <span className="text-xs text-gray-500 truncate flex items-center gap-1" title={data.url}>
              <LinkIcon className="w-3 h-3" />
              {data.url.replace(/^https?:\/\//, '').slice(0, 32)}
              {data.url.length > 32 ? '…' : ''}
            </span>
          )}
        </div>

        <div className="flex items-center justify-between pt-2 border-t">
          <span className="text-xs text-gray-500">Integration</span>
          <span className="text-xs text-gray-500">$0.000</span>
        </div>
      </div>

      <Handle type="source" position={Position.Right} className="w-3 h-3 !bg-orange-500" />
    </div>
  );
}

export default memo(NotifyStepNode);

